import React, { memo, useCallback, useEffect } from 'react';
import { showFullScreenError } from '../../../store/actions/error';
import GridSection from '../../../components/grid-section';
import InfiniteScroll from '../../../components/infinite-scroll';
import MilestonesRow from './milestones';
import {
  pastChallengesTitle,
  myCurrentChallengesTitle,
  myChallengesTitle,
} from '../../../i18n/translations';
import LibraryFeaturedContent from '../../../components/library-featured-content';
import { getSearchState } from '../../search/state/search-state';
import {
  ICurrentOrPast,
  IUserLibraryProgramFormatted,
  IUserLibraryProgramItem,
} from '@ifit/gateway-wolf-athenaeum-service';
import {
  useGetChallengesQuery,
  useGetInfiniteChallengesQuery,
} from '../../../client/query/challenges';
import Loading from '@ifit/mp-loading';
import flatten from 'lodash.flatten';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useToolbarTitle from '@ifit/hooks/useToolbarTitle';
import { setLastTitle } from '../../../store/to-native';
import { useFeatureFlags } from '../../../helpers/good-cop-helper';

import { getChallengesEntry } from '../../../store/selectors';
import getStore from '../../../store/index';

const featureFlag = 'featureflag://flag?key=trophy-case-enabled&type=bool';

type ChallengeFeaturedContent =
  | Omit<IUserLibraryProgramItem, 'item'>
  | { items: { item: IUserLibraryProgramFormatted }[] };

const MyChallenges: React.FC = () => {
  const dispatch = useDispatch();
  const currentFeaturedContent = useGetChallengesQuery(ICurrentOrPast.Current);
  const currentContent = useGetInfiniteChallengesQuery(
    ICurrentOrPast.Current,
    6,
  );
  const pastContent = useGetInfiniteChallengesQuery(ICurrentOrPast.Past);
  const location = useLocation();
  const isChallengeEntry = getChallengesEntry(getStore().getState());

  useToolbarTitle(
    myChallengesTitle(),
    {
      buttonStyle:
        location.pathname === '/my-challenges' || isChallengeEntry
          ? 'back'
          : 'hamburger',
    },
    () => setLastTitle(myChallengesTitle()),
  );

  useEffect(() => {
    getSearchState().setViewContext({});
  }, []);

  const loadMorePast = useCallback(() => pastContent.fetchNextPage(), [
    pastContent,
  ]);

  const loadMoreCurrent = useCallback(() => currentContent.fetchNextPage(), [
    currentContent,
  ]);

  const featureFlags = useFeatureFlags([featureFlag]);
  const isTophyCaseEnabled = featureFlags[featureFlag]
    ? (featureFlags[featureFlag] as boolean)
    : false;

  if (
    currentFeaturedContent.isLoading ||
    pastContent.isLoading ||
    currentContent.isLoading
  ) {
    return <Loading absolute data-testid={'loading-spinner'} />;
  }

  if (
    currentFeaturedContent.isError &&
    pastContent.isError &&
    currentContent.isError
  ) {
    dispatch(showFullScreenError());
    return null;
  }

  const { pages: currentPages } = currentContent?.data || { pages: [] as any };
  const currentHasMoreItems =
    currentPages?.length &&
    currentPages?.[currentPages?.length - 1]?.hasMoreItems;
  const currentChallengesItems = flatten(currentPages?.map(c => c.data));

  const { pages: pastPages } = pastContent?.data || { pages: [] as any };
  const pastHasMoreItems =
    pastPages?.length && pastPages?.[pastPages?.length - 1]?.hasMoreItems;
  const pastChallengesItems = flatten(pastPages?.map(p => p.data));

  const challengeFeaturedContent: ChallengeFeaturedContent[] = currentFeaturedContent?.data?.data?.map(
    ({ item, ...challenge }) => ({
      ...challenge,
      items: [{ item }],
    }),
  );

  return (
    <div className="challenges-view" data-testid="challenges-view">
      {challengeFeaturedContent?.length > 0 &&
        !currentFeaturedContent.isError && (
          <LibraryFeaturedContent featuredItems={challengeFeaturedContent} />
        )}

      {!isTophyCaseEnabled && <MilestonesRow />}

      {currentChallengesItems?.length > 0 && (
        <InfiniteScroll
          reset={currentPages?.length === 1}
          loadMore={loadMoreCurrent}
          hasMoreItems={currentHasMoreItems}
          libraryCards={currentChallengesItems}
          infiniteButtonEnable
        >
          <GridSection title={myCurrentChallengesTitle()} showCategory />
        </InfiniteScroll>
      )}
      {pastChallengesItems.length > 0 && (
        <InfiniteScroll
          reset={pastPages?.length === 1}
          loadMore={loadMorePast}
          hasMoreItems={pastHasMoreItems}
          libraryCards={pastChallengesItems}
        >
          <GridSection title={pastChallengesTitle()} showCategory />
        </InfiniteScroll>
      )}
    </div>
  );
};

export default memo(MyChallenges);
